import type React from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import type { TimelineEntity, ViewState } from "./Timeline.types";
import { getCategoryColor } from "./utils/colors";

const ROW_HEIGHT = 50;
const ENTITY_START_Y = 42;
const MINIMAP_WIDTH = 280;
const MINIMAP_HEIGHT = 130;
const MINIMAP_PADDING = 10;
const AXIS_HEIGHT = 18;
const AXIS_FONT = "bold 10px -apple-system, BlinkMacSystemFont, sans-serif";

type TimelineMinimapProps = {
  entities: TimelineEntity[];
  viewState: ViewState;
  mainWidth: number;
  mainHeight: number;
  yearToX: (year: number) => number;
  onNavigate: (viewState: ViewState) => void;
};

export const TimelineMinimap: React.FC<TimelineMinimapProps> = ({
  entities,
  viewState,
  mainWidth,
  mainHeight,
  yearToX,
  onNavigate,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  const worldBounds = useMemo(() => {
    if (entities.length === 0) {
      // No entities — derive bounds from the current viewport so the user can
      // still navigate the minimap instead of being stuck on an empty screen.
      if (viewState.scale <= 0) return null;
      const vpLeft = (0 - viewState.offsetX) / viewState.scale;
      const vpRight = (mainWidth - viewState.offsetX) / viewState.scale;
      const span = Math.max(1, vpRight - vpLeft);
      // Show 5× the viewport span (2× padding each side) so there's room to navigate.
      return {
        worldMinX: vpLeft - 2 * span,
        worldMaxX: vpRight + 2 * span,
        worldMinY: ENTITY_START_Y,
        worldMaxY: ENTITY_START_Y + ROW_HEIGHT,
      };
    }

    let wMinX = Infinity;
    let wMaxX = -Infinity;
    let maxRow = 0;

    entities.forEach((entity) => {
      const x = yearToX(entity.start.year);
      wMinX = Math.min(wMinX, x);
      wMaxX = Math.max(wMaxX, x);
      if (entity.end && entity.end.year !== entity.start.year) {
        const endX = yearToX(entity.end.year);
        wMaxX = Math.max(wMaxX, endX);
      }
      maxRow = Math.max(maxRow, entity.rowNumber);
    });

    const pad = 50;
    return {
      worldMinX: wMinX - pad,
      worldMaxX: wMaxX + pad,
      worldMinY: ENTITY_START_Y,
      worldMaxY: ENTITY_START_Y + (maxRow + 1) * ROW_HEIGHT,
    };
  }, [entities, yearToX, viewState, mainWidth]);

  const minimapTransform = useMemo(() => {
    if (!worldBounds) return null;
    const { worldMinX, worldMaxX, worldMinY, worldMaxY } = worldBounds;
    const worldWidth = Math.max(1, worldMaxX - worldMinX);
    const worldHeight = Math.max(1, worldMaxY - worldMinY);
    const drawW = MINIMAP_WIDTH - MINIMAP_PADDING * 2;
    const drawH = MINIMAP_HEIGHT - MINIMAP_PADDING * 2 - AXIS_HEIGHT;

    return {
      scaleX: drawW / worldWidth,
      scaleY: drawH / worldHeight,
      worldMinX,
      worldMaxX,
      worldMinY,
    };
  }, [worldBounds]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !minimapTransform) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const dpr = window.devicePixelRatio || 1;
    if (canvas.width !== MINIMAP_WIDTH * dpr || canvas.height !== MINIMAP_HEIGHT * dpr) {
      canvas.width = MINIMAP_WIDTH * dpr;
      canvas.height = MINIMAP_HEIGHT * dpr;
    }
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.scale(dpr, dpr);
    ctx.clearRect(0, 0, MINIMAP_WIDTH, MINIMAP_HEIGHT);

    // Background with rounded corners
    ctx.fillStyle = "#F8FAFC";
    const r = 6;
    ctx.beginPath();
    ctx.moveTo(r, 0);
    ctx.lineTo(MINIMAP_WIDTH - r, 0);
    ctx.quadraticCurveTo(MINIMAP_WIDTH, 0, MINIMAP_WIDTH, r);
    ctx.lineTo(MINIMAP_WIDTH, MINIMAP_HEIGHT - r);
    ctx.quadraticCurveTo(MINIMAP_WIDTH, MINIMAP_HEIGHT, MINIMAP_WIDTH - r, MINIMAP_HEIGHT);
    ctx.lineTo(r, MINIMAP_HEIGHT);
    ctx.quadraticCurveTo(0, MINIMAP_HEIGHT, 0, MINIMAP_HEIGHT - r);
    ctx.lineTo(0, r);
    ctx.quadraticCurveTo(0, 0, r, 0);
    ctx.closePath();
    ctx.fill();
    ctx.strokeStyle = "#CBD5E1";
    ctx.lineWidth = 1;
    ctx.stroke();

    const { scaleX, scaleY, worldMinX, worldMaxX, worldMinY } = minimapTransform;
    const entityAreaTop = MINIMAP_PADDING + AXIS_HEIGHT;

    // Draw year axis labels at top — use entity world bounds, not viewport bounds.
    // Using viewport bounds caused all labels to map outside the minimap when the
    // viewport is far from the entity data (e.g. viewing CE while entities are BCE).
    ctx.font = AXIS_FONT;
    ctx.fillStyle = "#6B7280";
    ctx.textAlign = "center";
    const worldSpan = worldMaxX - worldMinX;
    const niceStep = (target: number) => {
      const p10 = 10 ** Math.floor(Math.log10(target));
      const cands = [1, 2, 2.5, 5].map((m) => m * p10);
      return cands.find((c) => c >= target) ?? p10 * 10;
    };
    const targetLabels = Math.max(2, Math.floor((MINIMAP_WIDTH - MINIMAP_PADDING * 2) / 60));
    const step = niceStep(Math.max(worldSpan / targetLabels, 1));
    const startYear = Math.floor(worldMinX / step) * step;

    for (let year = startYear; year <= worldMaxX + step; year += step) {
      const worldX = yearToX(year);
      const mx = MINIMAP_PADDING + (worldX - worldMinX) * scaleX;
      if (mx < MINIMAP_PADDING || mx > MINIMAP_WIDTH - MINIMAP_PADDING) continue;
      const label = year < 0 ? `${Math.abs(year).toLocaleString()} BC` : year.toLocaleString();
      ctx.fillText(label, mx, MINIMAP_PADDING + 12);

      // Subtle tick line
      ctx.strokeStyle = "#E5E7EB";
      ctx.lineWidth = 0.5;
      ctx.beginPath();
      ctx.moveTo(mx, entityAreaTop);
      ctx.lineTo(mx, MINIMAP_HEIGHT - MINIMAP_PADDING);
      ctx.stroke();
    }

    // Draw entities as small marks
    entities.forEach((entity) => {
      const worldX = yearToX(entity.start.year);
      const worldY = ENTITY_START_Y + entity.rowNumber * ROW_HEIGHT;
      const mx = MINIMAP_PADDING + (worldX - worldMinX) * scaleX;
      const my = entityAreaTop + (worldY - worldMinY) * scaleY;
      const color = getCategoryColor(entity.categories);

      if (entity.end && entity.end.year !== entity.start.year) {
        const endWorldX = yearToX(entity.end.year);
        const endMx = MINIMAP_PADDING + (endWorldX - worldMinX) * scaleX;
        const barWidth = Math.max(3, endMx - mx);
        ctx.fillStyle = color;
        ctx.globalAlpha = 0.7;
        ctx.fillRect(mx, my - 2, barWidth, 4);
      } else {
        ctx.fillStyle = color;
        ctx.globalAlpha = 0.8;
        ctx.beginPath();
        ctx.arc(mx, my, 2.5, 0, 2 * Math.PI);
        ctx.fill();
      }
    });

    ctx.globalAlpha = 1;

    // Viewport rectangle
    const vpWorldLeft = (0 - viewState.offsetX) / viewState.scale;
    const vpWorldRight = (mainWidth - viewState.offsetX) / viewState.scale;
    const vpWorldTop = 0 - viewState.offsetY;
    const vpWorldBottom = mainHeight - viewState.offsetY;

    const vpLeft = Math.max(0, MINIMAP_PADDING + (vpWorldLeft - worldMinX) * scaleX);
    const vpRight = Math.min(MINIMAP_WIDTH, MINIMAP_PADDING + (vpWorldRight - worldMinX) * scaleX);
    const vpTop = Math.max(entityAreaTop, entityAreaTop + (vpWorldTop - worldMinY) * scaleY);
    const vpBottom = Math.min(MINIMAP_HEIGHT, entityAreaTop + (vpWorldBottom - worldMinY) * scaleY);

    if (vpRight > vpLeft && vpBottom > vpTop) {
      ctx.fillStyle = "rgba(239, 68, 68, 0.06)";
      ctx.fillRect(vpLeft, vpTop, vpRight - vpLeft, vpBottom - vpTop);
      ctx.strokeStyle = "#EF4444";
      ctx.lineWidth = 1.5;
      ctx.strokeRect(vpLeft, vpTop, vpRight - vpLeft, vpBottom - vpTop);
    }
  }, [entities, viewState, mainWidth, mainHeight, yearToX, minimapTransform]);

  const handleMinimapMouse = useCallback(
    (e: React.MouseEvent, isDown?: boolean) => {
      if (!minimapTransform) return;
      if (isDown) setIsDragging(true);
      if (!isDragging && !isDown) return;

      const rect = canvasRef.current?.getBoundingClientRect();
      if (!rect) return;

      const mx = e.clientX - rect.left;
      const my = e.clientY - rect.top;
      const { scaleX, scaleY, worldMinX, worldMinY } = minimapTransform;
      const entityAreaTop = MINIMAP_PADDING + AXIS_HEIGHT;
      const worldX = (mx - MINIMAP_PADDING) / scaleX + worldMinX;
      const worldY = (my - entityAreaTop) / scaleY + worldMinY;

      onNavigate({
        ...viewState,
        offsetX: mainWidth / 2 - worldX * viewState.scale,
        offsetY: mainHeight / 2 - worldY,
      });
    },
    [minimapTransform, isDragging, viewState, mainWidth, mainHeight, onNavigate],
  );

  const handleMouseUp = useCallback(() => setIsDragging(false), []);

  return (
    <canvas
      ref={canvasRef}
      width={MINIMAP_WIDTH}
      height={MINIMAP_HEIGHT}
      style={{
        position: "absolute",
        bottom: 12,
        left: 12,
        width: MINIMAP_WIDTH,
        height: MINIMAP_HEIGHT,
        borderRadius: 6,
        boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
        cursor: "crosshair",
      }}
      onMouseDown={(e) => handleMinimapMouse(e, true)}
      onMouseMove={(e) => handleMinimapMouse(e)}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
    />
  );
};
